import { useState, useEffect } from 'react';
import { Card } from '../common';
import type { TarotCard as TarotCardType, DrawnCard } from '../../types/tarot';
import { getCardImagePath, formatCardName, getCardMeaning } from '../../utils/tarotLogic';
import '../../styles/tarot-animations.css';

interface TarotCardProps {
  card: TarotCardType | DrawnCard;
  isRevealed?: boolean;
  isSelected?: boolean;
  showMeaning?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  onClick?: () => void;
  className?: string;
  animationDelay?: number;
  enableFlipAnimation?: boolean;
  enableHoverEffects?: boolean;
  position?: number;
  spreadType?: string;
  // Decoration customization props
  equippedDeckId?: string | null;  // Custom card deck theme
  equippedCardBackPath?: string | null;  // Custom card back image
}

export function TarotCard({ 
  card, 
  isRevealed = true, 
  isSelected = false,
  showMeaning = false,
  size = 'md',
  onClick,
  className = '',
  animationDelay = 0,
  enableFlipAnimation = false, // Disabled by default
  enableHoverEffects = true,
  position,
  spreadType,
  equippedDeckId = null,
  equippedCardBackPath = null
}: TarotCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isFlipped, setIsFlipped] = useState(!isRevealed);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  
  const sizeClasses = {
    xs: 'w-[80px] h-[140px]',   // 1:1.75 ratio
    sm: 'w-[110px] h-[193px]',  // 1:1.75 ratio
    md: 'w-[140px] h-[245px]',  // 1:1.75 ratio
    lg: 'w-[170px] h-[298px]'   // 1:1.75 ratio
  };
  
  const isReversed = 'isReversed' in card && card.isReversed;
  
  // Determine card image path based on equipped deck or default
  const getCustomizedCardImagePath = (): string => {
    if (equippedDeckId && card.imagePath) {
      // Extract the card filename pattern from the existing imagePath
      // e.g., '/assets/decorations/card-images/rider-waite/rider-waite_00_the-fool.webp'
      // becomes 'celestial-wisdom_00_the-fool.webp' for the new deck
      const pathParts = card.imagePath.split('/');
      const filename = pathParts[pathParts.length - 1]; // e.g., 'rider-waite_00_the-fool.webp'
      
      // Replace the deck prefix in the filename
      const filenameParts = filename.split('_');
      if (filenameParts.length >= 3) {
        // Reconstruct with new deck ID: deckId_number_cardname.webp
        const cardNumber = filenameParts[1]; // e.g., '00'
        const cardSlug = filenameParts.slice(2).join('_'); // e.g., 'the-fool.webp'
        const newFilename = `${equippedDeckId}_${cardNumber}_${cardSlug}`;
        return `/assets/decorations/card-images/${equippedDeckId}/${newFilename}`;
      }
    }
    // Fall back to default card image path
    return getCardImagePath(card);
  };
  
  const cardImagePath = getCustomizedCardImagePath();
  
  // Determine card back image based on equipped card back or default
  const cardBack = equippedCardBackPath || '/images/cards/backs/default.webp';
  
  // Debug: Log card image path
  useEffect(() => {
    console.log('TarotCard - Card:', card.name);
    console.log('TarotCard - Image path:', cardImagePath);
    console.log('TarotCard - Has imagePath property:', 'imagePath' in card);
    if ('imagePath' in card) {
      console.log('TarotCard - Direct imagePath:', card.imagePath);
    }
  }, [card, cardImagePath]);
  
  // ANIMATIONS DISABLED - Skip flip animation logic
  // Handle flip animation when revealed state changes
  // useEffect(() => {
  //   if (enableFlipAnimation && isRevealed && isFlipped) {
  //     const timer = setTimeout(() => {
  //       setIsFlipped(false);
  //     }, animationDelay);
  //     return () => clearTimeout(timer);
  //   }
  // }, [isRevealed, enableFlipAnimation, animationDelay, isFlipped]);

  // ANIMATIONS DISABLED - Skip reveal animation
  // Handle reveal animation
  // useEffect(() => {
  //   if (isRevealed) {
  //     const timer = setTimeout(() => {
  //       setShouldAnimate(true);
  //     }, animationDelay);
  //     return () => clearTimeout(timer);
  //   }
  // }, [isRevealed, animationDelay]);
  
  const handleImageLoad = () => {
    setImageLoaded(true);
  };
  
  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const handleCardClick = () => {
    if (enableFlipAnimation && isFlipped) {
      setIsFlipped(false);
    }
    onClick?.();
  };

  // Generate animation classes
  const getAnimationClasses = () => {
    const classes = [];
    
    if (enableFlipAnimation) {
      classes.push('card-flip');
      if (isFlipped) classes.push('flipped');
    }
    
    if (shouldAnimate && isRevealed) {
      classes.push('card-reveal');
    }
    
    if (enableHoverEffects && onClick) {
      classes.push('card-hover');
    }
    
    if (spreadType === 'celtic-cross' && position) {
      classes.push('celtic-cross-card', `position-${position}`);
    }
    
    return classes.join(' ');
  };
  
  return (
    <div 
      className={`relative ${sizeClasses[size]} ${className} ${getAnimationClasses()}`}
      style={{ 
        animationDelay: `${animationDelay}ms`,
        '--delay': `${(position || 0) * 0.2}s`
      } as React.CSSProperties}
    >
      {/* FLIP ANIMATION DISABLED - Always show card front */}
      {false && enableFlipAnimation ? (
        <div className="card-flip-inner h-full">
          {/* Card Back */}
          <Card
            padding="none"
            className={`
              card-flip-front h-full cursor-pointer
              ${isSelected ? 'ring-2 ring-primary-500 shadow-lg' : ''}
              ${isReversed ? 'transform rotate-180' : ''}
            `}
            onClick={handleCardClick}
          >
            <div className="relative w-full h-full">
              <img
                src={cardBack}
                alt="Card back"
                className="w-full h-full object-cover"
              />
            </div>
          </Card>

          {/* Card Front */}
          <Card
            padding="none"
            className={`
              card-flip-back h-full cursor-pointer
              ${isSelected ? 'ring-2 ring-primary-500 shadow-lg' : ''}
              ${isReversed ? 'transform rotate-180' : ''}
            `}
            onClick={handleCardClick}
          >
            <div className="relative w-full h-full">
              {!imageLoaded && (
                <div className="absolute inset-0 bg-dark-700 animate-pulse flex items-center justify-center">
                  <span className="text-2xl">🎴</span>
                </div>
              )}
              
              <img
                src={cardImagePath}
                alt={card.name}
                className={`
                  w-full h-full object-cover transition-opacity duration-300
                  ${imageLoaded ? 'opacity-100' : 'opacity-0'}
                  ${imageError ? 'hidden' : ''}
                `}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
              
              {/* Fallback for missing images */}
              {imageError && (
                <div className="absolute inset-0 bg-dark-700 flex flex-col items-center justify-center text-center p-2">
                  <span className="text-lg mb-1">🎴</span>
                  <span className="text-xs text-gray-400 leading-tight">
                    {card.name}
                  </span>
                </div>
              )}
              
              {/* Card Name Overlay (for small sizes) */}
              {(size === 'xs' || size === 'sm') && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 text-center">
                  {formatCardName(card)}
                </div>
              )}
              
              {/* Reversal Indicator */}
              {isReversed && (
                <div className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1 rounded">
                  R
                </div>
              )}
            </div>
          </Card>
        </div>
      ) : (
        <Card
          padding="none"
          className={`
            h-full cursor-pointer rounded-xl overflow-hidden
            ${isSelected ? 'ring-2 ring-primary-500 shadow-lg' : ''}
            ${enableHoverEffects && onClick ? 'hover:shadow-lg hover:scale-105' : ''}
            ${isReversed ? 'transform rotate-180' : ''}
          `}
          onClick={handleCardClick}
        >
          {/* Card Image */}
          <div className="relative w-full h-full bg-white rounded-xl">
            {!imageLoaded && (
              <div className="absolute inset-0 bg-dark-700 animate-pulse flex items-center justify-center rounded-xl">
                <span className="text-2xl">🎴</span>
              </div>
            )}
            
            <img
              src={isRevealed ? cardImagePath : cardBack}
              alt={isRevealed ? card.name : 'Card back'}
              className={`
                w-full h-full object-contain transition-opacity duration-300 rounded-xl
                ${imageLoaded ? 'opacity-100' : 'opacity-0'}
                ${imageError ? 'hidden' : ''}
              `}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            
            {/* Fallback for missing images */}
            {imageError && (
              <div className="absolute inset-0 bg-dark-700 flex flex-col items-center justify-center text-center p-2 rounded-xl">
                <span className="text-lg mb-1">🎴</span>
                <span className="text-xs text-gray-400 leading-tight">
                  {card.name}
                </span>
              </div>
            )}
            
            {/* Card Name Overlay (for small sizes) */}
            {(size === 'xs' || size === 'sm') && isRevealed && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 text-center rounded-b-xl">
                {formatCardName(card)}
              </div>
            )}
            
            {/* Reversal Indicator */}
            {isReversed && isRevealed && (
              <div className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1 rounded">
                R
              </div>
            )}
          </div>
        </Card>
      )}
      
      {/* Card Details (for larger sizes) */}
      {(size === 'md' || size === 'lg') && isRevealed && (
        <div className="mt-2 text-center">
          <h3 className="text-sm font-semibold text-white">
            {formatCardName(card)}
          </h3>
          
          {showMeaning && 'meanings' in card && (
            <div className="mt-1">
              <div className="text-xs text-gray-400 mb-1">
                {'positionMeaning' in card && card.positionMeaning}
              </div>
              <div className="text-xs text-primary-400">
                {getCardMeaning(card as DrawnCard).slice(0, 2).join(', ')}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}